import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BellRing, Bell, Plane, ShieldAlert } from "lucide-react";

const LEVELS = [
  {
    value: "critical",
    label: "Endast kritiska",
    desc: "Inställt, omdirigerat, nödsignal, Schengen↔Utrikes-zonbyte, anslutning hotad, landat.",
    Icon: ShieldAlert,
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
  },
  {
    value: "standard",
    label: "Standard",
    desc: "Allt kritiskt + gate, ombordstigning, sista utrop, gate stängd, terminal, bagageband, startat, sjunker, försening, status.",
    Icon: Bell,
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
  },
  {
    value: "all",
    label: "Allt (geek)",
    desc: "Allt ovan + milstolpar: taxar mot bana, slutlig inflygning.",
    Icon: BellRing,
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
  },
];

export function FlightNotifLevelDialog({ open, onOpenChange, targetUser, onSaved }) {
  const initial = targetUser?.flight_notif_level || "standard";
  const [selected, setSelected] = useState(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!targetUser?.id) return;
    setBusy(true);
    try {
      await api.put(`/users/${targetUser.id}/flight-notif-level`, {
        level: selected,
      });
      toast.success("Bevakningsnivå sparad");
      onSaved?.(selected);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara nivån");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="flight-notif-level-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane size={18} className="text-[color:var(--moss)]" />
            Flygnotis-nivå
          </DialogTitle>
          <DialogDescription>
            {targetUser?.name
              ? `Välj vilka flygpushar ${targetUser.name.split(" ")[0]} ska få.`
              : "Välj vilka flygpushar du ska få."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-1">
          {LEVELS.map(({ value, label, desc, Icon, color, bg }) => {
            const isSel = selected === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setSelected(value)}
                data-testid={`flight-notif-level-option-${value}`}
                className={`w-full text-left rounded-lg border p-3 transition-all ${
                  isSel
                    ? `${bg} ring-2 ring-[color:var(--moss)]`
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon size={18} className={`${color} mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${color}`}>{label}</span>
                      {isSel && (
                        <span className="text-[10px] uppercase tracking-wider text-[color:var(--moss)] font-semibold">
                          Vald
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[color:var(--text-secondary)] mt-1 leading-snug">
                      {desc}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="flight-notif-level-cancel"
          >
            Avbryt
          </Button>
          <Button
            onClick={save}
            disabled={busy || selected === initial}
            className="bg-[color:var(--moss)] hover:bg-[#1f421f]"
            data-testid="flight-notif-level-save"
          >
            {busy ? "Sparar…" : "Spara"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
